import { Component, OnInit, OnDestroy, ViewEncapsulation, ElementRef, ViewChild, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ServicesService } from '../../services/requests/services.service';
import { ModalClickOutsideService } from '../../services/modal-click-outside.service';

declare function showNotif(message: string, type?: string): void;

@Component({
  selector: 'app-services',
  templateUrl: './services.component.html',
  styleUrls: ['./services.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class ServicesComponent implements OnInit, OnDestroy, AfterViewInit {
  services: any[] = [];
  filtered: any[] = [];
  paginatedServices: any[] = [];
  platforms: string[] = [];
  selectedPlatform: string = '';
  isLoading = false;

  currentPage = 1;
  itemsPerPage = 100;
  totalPages = 1;
  selectedServices: Set<string> = new Set();
  selectAll = false;
  sortField: string = '';
  sortDirection: 'asc' | 'desc' = 'asc';
  private _searchTerm: string = '';
  get searchTerm(): string {
    return this._searchTerm;
  }
  set searchTerm(value: string) {
    this._searchTerm = value;
    this.currentPage = 1;
    this.applyFilter();
  }

  showBulkEditModal = false;
  bulkAvailableToSell: boolean | null = null;
  ruleField1: 'price' | 'totalFree' = 'price';
  ruleOperator1: '>' | '<' | '>=' | '<=' | '==' = '>';
  ruleValue1: number = 0;
  ruleLogic: 'AND' | 'OR' = 'AND';
  ruleField2: 'price' | 'totalFree' = 'totalFree';
  ruleOperator2: '>' | '<' | '>=' | '<=' | '==' = '>';
  ruleValue2: number = 0;
  ruleResult: boolean = true;

  @ViewChild('bulkEditModalContent', { static: false }) bulkEditModalContent!: ElementRef;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private servicesService: ServicesService,
    private modalClickOutsideService: ModalClickOutsideService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (localStorage.getItem('isAdmin') !== 'true') {
      this.router.navigate(['/suivi']);
    }

    const savedSort = localStorage.getItem('localStorage_SP-Services');
    if (savedSort) {
      try {
        const sortData = JSON.parse(savedSort);
        this.sortField = sortData.field || '';
        this.sortDirection = sortData.direction || 'asc';
      } catch (e) {
        console.error('Erreur lors de la récupération du tri services :', e);
      }
    }

    this.getServices();
  }

  ngAfterViewInit(): void {
    this.updateModalRegistration();
  }

  ngOnDestroy(): void {
    document.body.style.overflow = '';
    if (this.bulkEditModalContent) {
      this.modalClickOutsideService.unregisterModal(this.bulkEditModalContent);
    }
  }

  private updateModalRegistration(): void {
    if (this.bulkEditModalContent) {
      if (this.showBulkEditModal) {
        this.modalClickOutsideService.registerModal(this.bulkEditModalContent, () => this.closeBulkEditModal());
      } else {
        this.modalClickOutsideService.unregisterModal(this.bulkEditModalContent);
      }
    }
  }

  private updateScroll(): void {
    document.body.style.overflow = this.showBulkEditModal ? 'hidden' : '';
  }

  get selectedServicesArray(): string[] {
    return Array.from(this.selectedServices);
  }

  getServiceName(serviceId: string): string {
    const service = this.services.find(s => s._id === serviceId);
    return service ? `${service.platform} - ${service.service}` : serviceId;
  }

  getServices(): void {
    this.isLoading = true;

    this.servicesService.getAll().subscribe({
      next: (response: any) => {
        this.isLoading = false;

        if (response.status) {
          this.services = response.data?.services || [];
          this.platforms = [...new Set(this.services.map(s => s.platform).filter(p => p))].sort();
          this.applyFilter();
        } else {
          showNotif(response.message || "❌ Erreur lors de la récupération", 'error');
        }
      },
      error: (error: any) => {
        this.isLoading = false;
        console.error("Erreur getServices() :", error);
        showNotif(error?.error?.message || "Erreur chargement services", 'error');
      }
    });
  }

  onPlatformChange(): void {
    this.currentPage = 1;
    this.applyFilter();
  }

  toggleSort(field: string): void {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDirection = 'asc';
    }
    
    try {
      localStorage.setItem('localStorage_SP-Services', JSON.stringify({
        field: this.sortField,
        direction: this.sortDirection
      }));
    } catch (e) {
      console.error('Erreur lors de la sauvegarde du tri services :', e);
    }
    
    this.currentPage = 1;
    this.applyFilter();
  }

  applyFilter(): void {
    let result = [...this.services];
    
    const searchTerm = this._searchTerm.toLowerCase().trim();
    if (searchTerm) {
      result = result.filter(s => {
        const platformMatch = s.platform?.toLowerCase().includes(searchTerm);
        const serviceMatch = s.service?.toLowerCase().includes(searchTerm);
        return platformMatch || serviceMatch;
      });
    }
    
    if (this.selectedPlatform && this.selectedPlatform !== '') {
      result = result.filter(s => s.platform === this.selectedPlatform);
    }

    if (this.sortField) {
      result.sort((a, b) => {
        let valueA: any;
        let valueB: any;
        
        switch (this.sortField) {
          case 'platform':
            valueA = a.platform?.toLowerCase() || '';
            valueB = b.platform?.toLowerCase() || '';
            return this.sortDirection === 'asc' 
              ? valueA.localeCompare(valueB) 
              : valueB.localeCompare(valueA);
          
          case 'service':
            valueA = a.service?.toLowerCase() || '';
            valueB = b.service?.toLowerCase() || '';
            return this.sortDirection === 'asc' 
              ? valueA.localeCompare(valueB) 
              : valueB.localeCompare(valueA);
          
          case 'totalFree':
            valueA = a.totalFree || 0;
            valueB = b.totalFree || 0;
            return this.sortDirection === 'asc' 
              ? valueA - valueB 
              : valueB - valueA;
          
          case 'price':
            valueA = a.price || 0;
            valueB = b.price || 0;
            return this.sortDirection === 'asc' 
              ? valueA - valueB 
              : valueB - valueA;
          
          case 'availableToSell':
            valueA = a.availableToSell ? 1 : 0;
            valueB = b.availableToSell ? 1 : 0;
            return this.sortDirection === 'asc' 
              ? valueA - valueB 
              : valueB - valueA;
          
          default:
            return 0;
        }
      });
    }

    this.filtered = result;
    this.totalPages = Math.max(1, Math.ceil(this.filtered.length / this.itemsPerPage));
    
    if (this.currentPage > this.totalPages) {
      this.currentPage = 1;
    }
    
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    this.paginatedServices = this.filtered.slice(startIndex, endIndex);
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.applyFilter();
  }

  updateAvailableToSell(service: any): void {
    const newValue = !service.availableToSell;
    
    this.servicesService.updateAvailableToSell({
      id: service._id,
      availableToSell: newValue
    }).subscribe({
      next: (response: any) => {
        if (response.status) {
          service.availableToSell = newValue;
          showNotif('✅ Service mis à jour', 'success');
        } else {
          service.availableToSell = !newValue;
          showNotif(response.message || "Erreur lors de la mise à jour", 'error');
        }
      },
      error: (error: any) => {
        service.availableToSell = !newValue;
        console.error("❌ Erreur updateAvailableToSell :", error);
        showNotif("Erreur serveur", 'error');
      }
    });
  }

  formatNumber(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return '0';
    }
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  }

  private updateSelectAllState(): void {
    this.selectAll = this.paginatedServices.length > 0 && this.paginatedServices.every(s => this.selectedServices.has(s._id));
  }

  toggleSelectAll(): void {
    const shouldSelectAll = !this.selectAll;
    this.paginatedServices.forEach(s => {
      if (shouldSelectAll) {
        this.selectedServices.add(s._id);
      } else {
        this.selectedServices.delete(s._id);
      }
    });
    this.updateSelectAllState();
  }

  toggleServiceSelection(serviceId: string): void {
    if (this.selectedServices.has(serviceId)) {
      this.selectedServices.delete(serviceId);
    } else {
      this.selectedServices.add(serviceId);
    }
    this.updateSelectAllState();
  }

  isServiceSelected(serviceId: string): boolean {
    return this.selectedServices.has(serviceId);
  }

  private checkBatchComplete(state: { completed: number; errors: number; total: number }): void {
    if (state.completed === state.total) {
      this.isLoading = false;
      if (state.errors === 0) {
        showNotif(`✅ ${state.total} service(s) mis à jour`, 'success');
      } else {
        showNotif(`⚠️ ${state.total - state.errors} service(s) mis à jour, ${state.errors} erreur(s)`, 'error');
      }
      this.selectedServices.clear();
      this.selectAll = false;
      this.closeBulkEditModal();
    }
  }

  openBulkEditModal(): void {
    if (this.selectedServices.size === 0) {
      showNotif('❗ Veuillez sélectionner au moins un service', 'error');
      return;
    }
    this.bulkAvailableToSell = null;
    this.ruleField1 = 'price';
    this.ruleOperator1 = '>';
    this.ruleValue1 = 0;
    this.ruleLogic = 'AND';
    this.ruleField2 = 'totalFree';
    this.ruleOperator2 = '>';
    this.ruleValue2 = 0;
    this.ruleResult = true;
    this.showBulkEditModal = true;
    this.updateScroll();
    setTimeout(() => {
      this.updateModalRegistration();
    }, 0);
  }

  closeBulkEditModal(): void {
    if (!this.isLoading) {
      this.showBulkEditModal = false;
      this.bulkAvailableToSell = null;
      this.updateScroll();
      this.updateModalRegistration();
    }
  }

  applyBulkChanges(): void {
    const selectedIds = Array.from(this.selectedServices);
    if (selectedIds.length === 0) {
      showNotif('Aucun service sélectionné', 'error');
      return;
    }

    const servicesToUpdate = this.services.filter(s => selectedIds.includes(s._id));
    let servicesToModify: any[] = [];

    if (this.ruleValue1 !== 0 || this.ruleValue2 !== 0) {
      servicesToModify = servicesToUpdate.filter(service => {
        const value1 = this.ruleField1 === 'price' ? service.price : service.totalFree;
        const value2 = this.ruleField2 === 'price' ? service.price : service.totalFree;

        const condition1 = this.evaluateCondition(value1, this.ruleOperator1, this.ruleValue1);
        const condition2 = this.evaluateCondition(value2, this.ruleOperator2, this.ruleValue2);

        const matches = this.ruleLogic === 'AND' 
          ? condition1 && condition2 
          : condition1 || condition2;

        return matches;
      });

      if (servicesToModify.length === 0) {
        showNotif('Aucun service ne correspond aux conditions', 'error');
        return;
      }
    } else {
      servicesToModify = servicesToUpdate;
    }

    let valueToApply: boolean | null = null;
    if (this.bulkAvailableToSell !== null) {
      valueToApply = this.bulkAvailableToSell;
    } else if (servicesToModify.length > 0) {
      valueToApply = this.ruleResult;
    } else {
      showNotif('Veuillez sélectionner une action', 'error');
      return;
    }

    this.isLoading = true;
    const batchState = { completed: 0, errors: 0, total: servicesToModify.length };

    servicesToModify.forEach(service => {
      if (service.availableToSell !== valueToApply) {
        this.servicesService.updateAvailableToSell({
          id: service._id,
          availableToSell: valueToApply!
        }).subscribe({
          next: (response: any) => {
            batchState.completed++;
            if (response.status) {
              service.availableToSell = valueToApply!;
            } else {
              batchState.errors++;
            }
            this.checkBatchComplete(batchState);
          },
          error: () => {
            batchState.completed++;
            batchState.errors++;
            this.checkBatchComplete(batchState);
          }
        });
      } else {
        batchState.completed++;
        this.checkBatchComplete(batchState);
      }
    });
  }

  private evaluateCondition(value: number, operator: string, threshold: number): boolean {
    switch (operator) {
      case '>': return value > threshold;
      case '<': return value < threshold;
      case '>=': return value >= threshold;
      case '<=': return value <= threshold;
      case '==': return value === threshold;
      default: return false;
    }
  }
}

